import { HttpTypes } from "@medusajs/types"

export const BRAND_HANDLES = [
  "mikrotik",
  "ubiquiti",
  "ruijie",
  "dlink",
] as const

export type BrandHandle = (typeof BRAND_HANDLES)[number]

const BRAND_ORDER = new Map(
  BRAND_HANDLES.map((handle, index) => [handle, index])
)

/** Prefer these line name fragments when picking featured product lines. */
const LINE_PRIORITY = [
  "switch",
  "wifi",
  "wireless",
  "router",
  "gateway",
  "cloud",
  "lte",
  "5g",
  "sfp",
  "access",
]

function isBrandHandle(handle?: string | null): handle is BrandHandle {
  return !!handle && BRAND_ORDER.has(handle as BrandHandle)
}

export function getBrandCategories(
  categories: HttpTypes.StoreProductCategory[]
): HttpTypes.StoreProductCategory[] {
  return categories
    .filter(
      (category) =>
        !category.parent_category && isBrandHandle(category.handle)
    )
    .sort(
      (a, b) =>
        (BRAND_ORDER.get(a.handle as BrandHandle) ?? 99) -
        (BRAND_ORDER.get(b.handle as BrandHandle) ?? 99)
    )
}

function lineScore(name: string): number {
  const lower = name.toLowerCase()
  const index = LINE_PRIORITY.findIndex((token) => lower.includes(token))
  return index === -1 ? LINE_PRIORITY.length + 1 : index
}

export function getFeaturedCategoryLines(
  categories: HttpTypes.StoreProductCategory[],
  limit = 8
): HttpTypes.StoreProductCategory[] {
  const brands = getBrandCategories(categories)
  const brandIds = new Set(brands.map((brand) => brand.id))
  const brandById = new Map(brands.map((brand) => [brand.id, brand]))

  const fromFlat = categories.filter((category) => {
    const parentId = category.parent_category?.id
    return !!parentId && brandIds.has(parentId)
  })

  const fromNested = brands.flatMap((brand) =>
    (brand.category_children || []).map((child) => ({
      ...child,
      parent_category: child.parent_category || {
        id: brand.id,
        name: brand.name,
        handle: brand.handle,
      },
    }))
  )

  const byId = new Map<string, HttpTypes.StoreProductCategory>()
  for (const line of [...fromFlat, ...fromNested]) {
    if (!line.id || byId.has(line.id)) {
      continue
    }
    // Ensure parent name is available for UI
    if (!line.parent_category?.name && line.parent_category?.id) {
      const parent = brandById.get(line.parent_category.id)
      if (parent) {
        line.parent_category = {
          ...line.parent_category,
          name: parent.name,
          handle: parent.handle,
        }
      }
    }
    byId.set(line.id, line)
  }

  const lines = Array.from(byId.values()).sort((a, b) => {
    const byScore = lineScore(a.name) - lineScore(b.name)
    if (byScore !== 0) {
      return byScore
    }
    return a.name.localeCompare(b.name)
  })

  const seen = new Set<string>()
  const picked: HttpTypes.StoreProductCategory[] = []

  for (const line of lines) {
    const key = line.name.toLowerCase()
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    picked.push(line)
    if (picked.length >= limit) {
      break
    }
  }

  return picked
}

/** Serializable brand line for mega menu / mobile accordion. */
export type MenuBrandLine = {
  id: string
  name: string
  handle: string
}

/** Brand entry for Products mega menu (featured order, then other roots). */
export type MenuBrand = {
  id: string
  name: string
  handle: string
  lines: MenuBrandLine[]
}

function toMenuLine(
  category: HttpTypes.StoreProductCategory
): MenuBrandLine | null {
  if (!category.id || !category.handle || !category.name) {
    return null
  }
  return {
    id: category.id,
    name: category.name,
    handle: category.handle,
  }
}

function resolveBrandLines(
  brand: HttpTypes.StoreProductCategory,
  categories: HttpTypes.StoreProductCategory[]
): MenuBrandLine[] {
  const byId = new Map<string, MenuBrandLine>()

  for (const child of brand.category_children || []) {
    const line = toMenuLine(child)
    if (line) {
      byId.set(line.id, line)
    }
  }

  for (const category of categories) {
    if (category.parent_category?.id !== brand.id) {
      continue
    }
    const line = toMenuLine(category)
    if (line) {
      byId.set(line.id, line)
    }
  }

  return Array.from(byId.values()).sort((a, b) => {
    const byScore = lineScore(a.name) - lineScore(b.name)
    if (byScore !== 0) {
      return byScore
    }
    return a.name.localeCompare(b.name)
  })
}

/**
 * All root brands for the Products mega menu.
 * Featured `BRAND_HANDLES` first, then other root categories by name.
 */
export function getMenuBrands(
  categories: HttpTypes.StoreProductCategory[]
): MenuBrand[] {
  const featured = getBrandCategories(categories)
  const featuredIds = new Set(featured.map((brand) => brand.id))

  const otherRoots = categories
    .filter(
      (category) =>
        !category.parent_category &&
        !!category.handle &&
        !featuredIds.has(category.id)
    )
    .sort((a, b) => a.name.localeCompare(b.name))

  return [...featured, ...otherRoots].map((brand) => ({
    id: brand.id,
    name: brand.name,
    handle: brand.handle,
    lines: resolveBrandLines(brand, categories),
  }))
}

/** Split brand lines into 1–4 columns (~perColumn items each). */
export function getBrandMenuColumns(
  lines: MenuBrandLine[],
  perColumn = 7
): MenuBrandLine[][] {
  if (lines.length === 0) {
    return []
  }

  const columnCount = Math.min(
    4,
    Math.max(1, Math.ceil(lines.length / perColumn))
  )
  const size = Math.ceil(lines.length / columnCount)
  const columns: MenuBrandLine[][] = []

  for (let i = 0; i < lines.length; i += size) {
    columns.push(lines.slice(i, i + size))
  }

  return columns
}
